// Client-side navigation between pages that opt in (docs/client-navigation.md).
// A click on a same-site link asks the server for the page area only
// (X-Smashladder-Navigation); the answer replaces #page_content under the header,
// which stays mounted. A page that hasn't opted in answers { fullLoad: true },
// and anything unexpected falls back to a normal page load.
//
// Back and forward ask the active page first: a page that pushes its own history
// entries (rankings paging, profile tabs) claims those URLs and handles them.
// Otherwise they show the page as it was first shown, from the pages this
// navigator has seen, without asking the server.

const NAVIGATING_CLASS = 'client-navigating';
const SLOW_CLASS = 'client-navigating-slow';
const VISITED_LIMIT = 20;

function withoutHash(href) {
	const url = new URL(href);
	url.hash = '';
	return url.href;
}

function classList(value) {
	return (value || '').split(/\s+/).filter(Boolean);
}

export function createClientNavigator({
	window,
	document,
	fetchPage,
	assign = (url) => window.location.assign(url),
	replace = (url) => window.location.replace(url),
	activatePageArea,
	deactivatePageArea,
	preparePageContent = () => Promise.resolve(),
	// Puts the page's locale prefix on a same-site URL (i18n/urlLocale), so
	// navigating from /ja/... stays in Japanese.
	localizeUrl = (url) => url,
	slowAfter = 300,
}) {
	const claims = new Set();
	const scrollPositions = new Map();
	const visited = new Map();
	let started = false;
	let areaActive = false;
	let lastHref = null;
	let serverBodyClasses = [];
	let request = null;
	let slowTimer = null;

	function pageArea() {
		return document.getElementById('page_content');
	}

	function takeDownPageArea() {
		if(areaActive) {
			areaActive = false;
			deactivatePageArea(pageArea());
		}
	}

	// The page's own head tags (services\seo\PageHead) carry data-page-meta;
	// the rest of the head belongs to the site and stays.
	function setHead(headHtml) {
		document.head.querySelectorAll('[data-page-meta]').forEach((tag) => tag.remove());
		const template = document.createElement('template');
		template.innerHTML = headHtml || '';
		document.head.appendChild(template.content);
	}

	// Classes scripts added to the body (open menus, chat state) stay; only the
	// ones the server set for the old page are swapped for the new page's.
	function setBody({ id, class: className }) {
		const nextClasses = classList(className);
		document.body.id = id || '';
		document.body.classList.remove(...serverBodyClasses);
		document.body.classList.add(...nextClasses);
		serverBodyClasses = nextClasses;
	}

	// The body is marked at once; it is marked slow only when the answer takes
	// long enough that dimming the old page won't read as a flicker.
	function showProgress() {
		window.clearTimeout(slowTimer);
		document.body.classList.add(NAVIGATING_CLASS);
		slowTimer = window.setTimeout(() => document.body.classList.add(SLOW_CLASS), slowAfter);
	}

	function hideProgress() {
		window.clearTimeout(slowTimer);
		document.body.classList.remove(NAVIGATING_CLASS, SLOW_CLASS);
	}

	// Least recently shown first, so the oldest page is the one dropped.
	function remember(url, payload) {
		const key = withoutHash(url);
		visited.delete(key);
		visited.set(key, payload);
		if(visited.size > VISITED_LIMIT) {
			visited.delete(visited.keys().next().value);
		}
	}

	// The page a full load printed, before anything brings it to life, as a
	// payload back and forward can show again.
	function currentPagePayload() {
		return {
			url: window.location.href,
			title: document.title,
			head: [...document.head.querySelectorAll('[data-page-meta]')].map((tag) => tag.outerHTML).join(''),
			body: { id: document.body.id, class: serverBodyClasses.join(' ') },
			content: pageArea().innerHTML,
		};
	}

	async function readPayload(response) {
		const contentType = response.headers.get('content-type') || '';
		if(!response.ok || !contentType.includes('application/json')) {
			return null;
		}
		const payload = await response.json();
		return payload && (payload.fullLoad || typeof payload.content === 'string') ? payload : null;
	}

	async function load(url, { push }) {
		if(request) {
			request.abort();
		}
		const current = new AbortController();
		request = current;
		showProgress();
		window.dispatchEvent(new window.CustomEvent('smashladder:navigating', { detail: { url } }));

		// A link always asks the server; back and forward show what was shown.
		let payload = push ? null : (visited.get(withoutHash(url)) || null);
		const restored = Boolean(payload);
		try {
			if(!payload) {
				payload = await readPayload(await fetchPage(url, { signal: current.signal }));
			}
			// The old page stays up until the new one can mount without waiting.
			if(payload && !payload.fullLoad) {
				await preparePageContent(payload.content);
			}
		}
		catch(error) {
			payload = null;
		}
		if(current.signal.aborted) {
			return;
		}
		request = null;

		// The progress stays up through a full load; pageshow clears it if the
		// browser brings this page back from its cache.
		if(!payload || payload.fullLoad) {
			if(push) {
				assign(url);
			}
			else {
				replace(url);
			}
			return;
		}

		hideProgress();
		const pageUrl = payload.url || url;
		remember(pageUrl, payload);
		if(push) {
			scrollPositions.set(window.location.href, window.scrollY);
		}
		takeDownPageArea();
		const area = pageArea();
		area.innerHTML = payload.content;
		document.title = payload.title || document.title;
		setHead(payload.head);
		setBody(payload.body || {});
		if(push) {
			window.history.pushState(null, '', pageUrl);
		}
		else if(pageUrl !== url) {
			window.history.replaceState(null, '', pageUrl);
		}
		lastHref = window.location.href;
		window.scrollTo(0, push ? 0 : (scrollPositions.get(lastHref) || 0));

		areaActive = true;
		await activatePageArea(area, { restored });
		window.dispatchEvent(new window.CustomEvent('smashladder:navigated', { detail: { url: lastHref } }));
	}

	function onClick(event) {
		if(event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
			return;
		}
		const link = event.target.closest && event.target.closest('a[href]');
		if(!link || (link.target && link.target !== '_self') || link.hasAttribute('download') || link.hasAttribute('data-full-load')) {
			return;
		}
		const url = new URL(link.href, window.location.href);
		if(url.origin !== window.location.origin) {
			return;
		}
		if(url.hash && withoutHash(url.href) === withoutHash(window.location.href)) {
			return;
		}
		event.preventDefault();
		load(localizeUrl(url.href), { push: true });
	}

	function onPopState() {
		const url = window.location.href;
		const previous = lastHref;
		lastHref = url;
		for(const claim of [...claims]) {
			if(claim(url)) {
				return;
			}
		}
		if(withoutHash(url) === withoutHash(previous)) {
			return;
		}
		scrollPositions.set(previous, window.scrollY);
		takeDownPageArea();
		load(url, { push: false });
	}

	function onPageShow(event) {
		if(event.persisted) {
			hideProgress();
		}
	}

	return {
		// Only a page the server marked navigable starts it; elsewhere links load
		// as they always have.
		start() {
			if(started || !document.body.hasAttribute('data-client-navigation')) {
				return false;
			}
			started = true;
			areaActive = true;
			lastHref = window.location.href;
			serverBodyClasses = classList(document.body.className);
			remember(lastHref, currentPagePayload());
			if('scrollRestoration' in window.history) {
				window.history.scrollRestoration = 'manual';
			}
			document.addEventListener('click', onClick);
			window.addEventListener('popstate', onPopState);
			window.addEventListener('pageshow', onPageShow);
			return true;
		},

		stop() {
			if(!started) {
				return;
			}
			started = false;
			if(request) {
				request.abort();
				request = null;
			}
			hideProgress();
			visited.clear();
			document.removeEventListener('click', onClick);
			window.removeEventListener('popstate', onPopState);
			window.removeEventListener('pageshow', onPageShow);
		},

		visit(href) {
			const url = localizeUrl(new URL(href, window.location.href).href);
			if(!started) {
				assign(url);
				return Promise.resolve();
			}
			return load(url, { push: true });
		},

		// claim(url) returns true when the active page shows url itself.
		claimLocations(claim) {
			claims.add(claim);
			return () => claims.delete(claim);
		},
	};
}
